import * as React from "react"

export const RECAPTCHA_ENTERPRISE_SITE_KEY =
  "6LchtJ8sAAAAAAg6MNkS4rh2FCKzWWaqXI6Y64vB"
export const RECAPTCHA_ENTERPRISE_SUBMIT_ACTION = "submit"

const RECAPTCHA_ENTERPRISE_SCRIPT_ID = "google-recaptcha-enterprise"
const RECAPTCHA_ENTERPRISE_SCRIPT_SRC = `https://www.google.com/recaptcha/enterprise.js?render=${encodeURIComponent(
  RECAPTCHA_ENTERPRISE_SITE_KEY,
)}`

interface GrecaptchaEnterpriseApi {
  ready: (callback: () => void) => void
  execute: (
    siteKey: string,
    options: {
      action: string
    },
  ) => Promise<string>
}

declare global {
  interface Window {
    grecaptcha?: {
      enterprise?: GrecaptchaEnterpriseApi
    }
  }
}

let recaptchaEnterprisePromise: Promise<GrecaptchaEnterpriseApi> | null = null

function loadRecaptchaEnterpriseApi() {
  if (typeof window === "undefined") {
    return Promise.reject(
      new Error("reCAPTCHA Enterprise is only available in the browser."),
    )
  }

  if (window.grecaptcha?.enterprise) {
    return new Promise<GrecaptchaEnterpriseApi>(resolve => {
      window.grecaptcha?.enterprise?.ready(() => {
        if (window.grecaptcha?.enterprise) {
          resolve(window.grecaptcha.enterprise)
        }
      })
    })
  }

  if (!recaptchaEnterprisePromise) {
    recaptchaEnterprisePromise = new Promise<GrecaptchaEnterpriseApi>(
      (resolve, reject) => {
        const resolveApi = () => {
          const enterpriseApi = window.grecaptcha?.enterprise

          if (!enterpriseApi) {
            recaptchaEnterprisePromise = null
            reject(new Error("reCAPTCHA Enterprise failed to initialize."))
            return
          }

          enterpriseApi.ready(() => resolve(enterpriseApi))
        }

        const handleError = () => {
          document.getElementById(RECAPTCHA_ENTERPRISE_SCRIPT_ID)?.remove()
          recaptchaEnterprisePromise = null
          reject(new Error("reCAPTCHA Enterprise failed to load."))
        }

        document.getElementById(RECAPTCHA_ENTERPRISE_SCRIPT_ID)?.remove()

        const script = document.createElement("script")
        script.id = RECAPTCHA_ENTERPRISE_SCRIPT_ID
        script.src = RECAPTCHA_ENTERPRISE_SCRIPT_SRC
        script.async = true
        script.defer = true
        script.addEventListener("load", resolveApi, { once: true })
        script.addEventListener("error", handleError, { once: true })
        document.head.appendChild(script)
      },
    )
  }

  return recaptchaEnterprisePromise
}

export function useRecaptchaEnterprise(preload = true) {
  const [isReady, setIsReady] = React.useState(
    typeof window !== "undefined" && Boolean(window.grecaptcha?.enterprise),
  )

  React.useEffect(() => {
    if (!preload) {
      return
    }

    let isCancelled = false

    loadRecaptchaEnterpriseApi()
      .then(() => {
        if (!isCancelled) {
          setIsReady(true)
        }
      })
      .catch(() => {
        if (!isCancelled) {
          setIsReady(false)
        }
      })

    return () => {
      isCancelled = true
    }
  }, [preload])

  const execute = React.useCallback(
    async (action = RECAPTCHA_ENTERPRISE_SUBMIT_ACTION) => {
      const enterpriseApi = await loadRecaptchaEnterpriseApi()
      const token = await enterpriseApi.execute(
        RECAPTCHA_ENTERPRISE_SITE_KEY,
        { action },
      )

      if (!token) {
        throw new Error("reCAPTCHA Enterprise returned an empty token.")
      }

      setIsReady(true)
      return token
    },
    [],
  )

  return {
    action: RECAPTCHA_ENTERPRISE_SUBMIT_ACTION,
    execute,
    isReady,
    siteKey: RECAPTCHA_ENTERPRISE_SITE_KEY,
  }
}
